/**
 * World modifiers — the read side of `{ kind: 'world-modifier' }` (issue #7).
 *
 * Three Codex unlocks granted a modifier id, `unlockedWorldModifiers()` had no
 * callers, and `WorldGenConfig` had no field to put one in. They are the Codex
 * track's only *mechanical* reward — everything else it grants is information —
 * so the track topped out at 110 and changed nothing about how a world was made.
 *
 * Two rules, both from the issue's design note, and both load-bearing:
 *
 * **Player-chosen, and visible before BEGIN RUN.** A modifier the player opted
 * into is a challenge; one that appears unannounced is a bug report. The chosen
 * set lives on the profile (`ProgressionState.worldModifiers`), is offered as
 * toggles by the run-setup screen, and is copied onto `RunState.modifiers` so a
 * run can always say what it was made with.
 *
 * **The effects are a flat numeric struct, resolved here.** `worldgen.ts` must
 * not know that "volatile-strata" is an unlock id, and this file must not know
 * what a chunk is. What crosses the seam is `WorldGenModifiers`: five numbers
 * and a material name list, with a documented neutral value for each. That keeps
 * the determinism contract intact — the struct is a pure function of the id set,
 * and `resolveWorldModifiers` is order-independent (every combinator is
 * multiplication or max), so the same seed and the same *set* of modifiers
 * always produce the same world no matter what order the toggles were clicked.
 *
 * Decoupled by design: no imports from world/. Material names are plain strings
 * in `sim/materialData.ts`'s vocabulary, checked by `tests/ids.test.ts`.
 */

import { UNLOCKS } from './progression';
import type { Progression } from './progression';

export type WorldModifierId = 'volatile-strata' | 'drowned-world' | 'thin-veil';

/**
 * The struct `WorldGenConfig.modifiers` carries.
 *
 * Every field is a *multiplier on an authored biome number* rather than an
 * absolute, because biomes already disagree by an order of magnitude about all
 * three (Sunken Vault's hazard density is 0.02, the Rotwood's is 0.4). An
 * absolute would flatten twelve authored identities into one; a multiplier
 * scales each biome's own character, which is what "Volatile Strata" should mean
 * in a biome that was already volatile.
 */
export interface WorldGenModifiers {
  /** Scales `BiomeDef.terrain.depositDensity` — how often a drift is laid. */
  depositDensityMul: number;
  /** Scales `BiomeDef.terrain.liquidLevel` — how many pools a chunk gets. */
  liquidLevelMul: number;
  /** Scales `BiomeDef.terrain.hazardDensity` — ambient hazard pockets. */
  hazardDensityMul: number;
  /**
   * 0..1. Probability that a deposit cell is replaced with a material from
   * `voidMaterials`. This is the one field that adds matter rather than scaling
   * it, which is why it is a probability and not a multiplier: at 0 the
   * substitution loop never runs and costs nothing.
   */
  voidBleed: number;
  /** What `voidBleed` substitutes in. Empty when `voidBleed` is 0. */
  voidMaterials: readonly string[];
  /**
   * 0..1. How strongly deposit fill is biased toward the flammable/explosive
   * end of a biome's palette. Distinct from `depositDensityMul`: density is HOW
   * OFTEN, this is WHAT — doubling coal-dust drifts and doubling drifts of the
   * local powder are different worlds, and Volatile Strata means the first.
   */
  volatileFillBias: number;
}

/** The world with no modifiers: every multiplier 1, every addition 0. */
export const NEUTRAL_WORLD_MODIFIERS: WorldGenModifiers = Object.freeze({
  depositDensityMul: 1,
  liquidLevelMul: 1,
  hazardDensityMul: 1,
  voidBleed: 0,
  voidMaterials: Object.freeze([]) as readonly string[],
  volatileFillBias: 0,
});

export interface WorldModifierDef {
  id: WorldModifierId;
  name: string;
  /** One sentence, in the run-setup screen's voice. */
  desc: string;
  /** The one-line warning: what this makes harder, stated plainly. */
  cost: string;
  /** The unlock that grants it — so a locked toggle can name its price. */
  unlock: string;
  effects: Partial<WorldGenModifiers>;
}

/**
 * The three, with the numbers the generator will actually apply.
 *
 * The magnitudes are chosen to be *visible in one screen* rather than
 * statistically detectable: a doubling of deposits is the difference between
 * walking past a coal bank and walking through one. Anything subtler and the
 * player cannot tell the modifier is on, which is the same failure as it not
 * being on.
 */
export const WORLD_MODIFIERS: readonly WorldModifierDef[] = [
  {
    id: 'volatile-strata',
    name: 'Volatile Strata',
    desc: 'Doubled explosive and accelerant deposits. Everything is a fuse.',
    cost: 'Your own fire spreads as far as theirs.',
    unlock: 'codex.mod.volatile',
    effects: { depositDensityMul: 2, volatileFillBias: 0.75 },
  },
  {
    id: 'drowned-world',
    name: 'Drowned World',
    desc: 'Liquid volumes scaled up. Fluid Adapt stops being optional.',
    cost: 'Flooded routes you cannot swim are dead ends.',
    unlock: 'codex.mod.drowned',
    effects: { liquidLevelMul: 2.5, hazardDensityMul: 1.4 },
  },
  {
    id: 'thin-veil',
    name: 'Thin Veil',
    desc: 'Void and temporal materials bleed into every biome.',
    cost: 'The Hollow Choir notices sooner, everywhere.',
    unlock: 'codex.mod.thin-veil',
    effects: {
      voidBleed: 0.18,
      // Named in `sim/materialData.ts`'s vocabulary. `tests/ids.test.ts` asserts
      // each one resolves, so a rename in the registry fails there rather than
      // silently substituting nothing.
      voidMaterials: ['VOID_MATTER', 'TEMPORAL_RESIDUE', 'VOIDGLASS'],
      hazardDensityMul: 1.25,
    },
  },
];

// `WORLD_MODIFIER_BY_ID` lived here and went with `describeWorldModifiers`
// below, which was its only reader. An index whose one lookup has been deleted
// is a slower way to spell `WORLD_MODIFIERS.find`.

export const WORLD_MODIFIER_IDS: readonly WorldModifierId[] = WORLD_MODIFIERS.map((m) => m.id);

/**
 * Combine a set of modifiers into one struct.
 *
 * Order-independent on purpose — multiplication commutes, `max` commutes, and
 * the material lists are unioned in authored order rather than argument order.
 * Determinism (issue #7's fourth acceptance criterion) is a property of the
 * *set*, so two players who toggled the same three modifiers in different orders
 * must get the same world, and a UI that reorders its own list must not change
 * anything. Unknown ids are skipped rather than raised: a modifier id that
 * outlived its definition costs the player that modifier, not the run.
 */
export function resolveWorldModifiers(ids: Iterable<string>): WorldGenModifiers {
  const held = new Set(ids);
  const voids = new Set<string>();
  let deposit = 1;
  let liquid = 1;
  let hazard = 1;
  let bleed = 0;
  let volatileBias = 0;

  // Authored order, not `held` order, so the union below is stable.
  for (const def of WORLD_MODIFIERS) {
    if (!held.has(def.id)) continue;
    const e = def.effects;
    if (e.depositDensityMul !== undefined) deposit *= e.depositDensityMul;
    if (e.liquidLevelMul !== undefined) liquid *= e.liquidLevelMul;
    if (e.hazardDensityMul !== undefined) hazard *= e.hazardDensityMul;
    if (e.voidBleed !== undefined) bleed = Math.max(bleed, e.voidBleed);
    if (e.volatileFillBias !== undefined) volatileBias = Math.max(volatileBias, e.volatileFillBias);
    for (const m of e.voidMaterials ?? []) voids.add(m);
  }

  return {
    depositDensityMul: deposit,
    liquidLevelMul: liquid,
    hazardDensityMul: hazard,
    voidBleed: bleed,
    voidMaterials: [...voids],
    volatileFillBias: volatileBias,
  };
}

/** True when the struct would change nothing, so worldgen can skip the work. */
export function isNeutral(m: WorldGenModifiers): boolean {
  return (
    m.depositDensityMul === 1 &&
    m.liquidLevelMul === 1 &&
    m.hazardDensityMul === 1 &&
    m.voidBleed === 0 &&
    m.volatileFillBias === 0
  );
}

/** The one call site of `Progression.unlockedWorldModifiers()`. */
export function unlockedWorldModifierIds(prog: Progression): WorldModifierId[] {
  const owned = new Set(prog.unlockedWorldModifiers());
  return WORLD_MODIFIER_IDS.filter((id) => owned.has(id));
}

/** What the run-setup screen renders as an opt-in toggle. */
export interface WorldModifierChoiceVM {
  id: string;
  name: string;
  desc: string;
  /** The one-line downside. Shown next to the toggle, not buried in a tooltip. */
  cost: string;
  /** Selected for the next run. */
  enabled: boolean;
  locked: boolean;
  lockHint?: string;
}

const MODIFIER_UNLOCK = new Map(
  UNLOCKS.filter((u) => u.grants.kind === 'world-modifier').map(
    (u) => [(u.grants as { modifier: string }).modifier, u] as const,
  ),
);

/**
 * Every modifier with its status — locked rows included, for the same reason the
 * archetype list keeps them (docs/02 §3.2 and `runSetupAdapter`'s header): a
 * reward you cannot see is not a motive.
 */
export function worldModifierChoices(prog: Progression): WorldModifierChoiceVM[] {
  const owned = new Set(unlockedWorldModifierIds(prog));
  const chosen = new Set(prog.effectiveWorldModifiers());
  return WORLD_MODIFIERS.map((m) => {
    const locked = !owned.has(m.id);
    const u = MODIFIER_UNLOCK.get(m.id);
    return {
      id: m.id,
      name: m.name,
      desc: m.desc,
      cost: m.cost,
      enabled: !locked && chosen.has(m.id),
      locked,
      lockHint: !locked || !u ? undefined : `Locked — reach ${u.cost} Codex.`,
    };
  });
}

// `describeWorldModifiers` lived here and was deleted by #118's sweep. "One
// line for the setup screen's footer and the run log": the setup screen has no
// footer — it renders `worldModifierChoices`, which gives each modifier its own
// row with its own name and cost, so a joined list of the same names below them
// would be the screen saying it twice — and the run log prints
// `describeGrants`, which counts modifiers rather than naming them.
//
// Note which half of this file was dead and which was not. `resolveWorldModifiers`
// — the function that turns the player's toggles into worldgen numbers — is
// also uncalled, and it is a *broken feature*: #7's toggles are collected,
// saved, and never reach the generator. That one is waiting on a `game.ts`
// hunk and is gated in `tests/liveness.test.ts`. This one was a caption.
